import { useCallback, useMemo } from "react";
import { ProductInputDto } from "@lib/ShiOneClient";
import { useGetAvailabilityForProducts } from "@features/orders-features/hooks/useGetAvailabilityForProducts";
import {
  getCategoryLabel,
  getCategoryType,
} from "@features/orders-features/functions/category";
import { LineItem } from "@features/orders-features/types/drawer";

export type UseProductAvailabilityResult = {
  isBackorder: (
    productDescriptor: string | undefined,
    productClass: string | undefined,
  ) => boolean;
  effectiveLines: LineItem[];
  isLoadingProductAvailability: boolean;
};

export function useProductAvailability(
  orderId: number | undefined,
  orgUnitId: number | undefined,
  items: LineItem[],
): UseProductAvailabilityResult {
  const processingItems = useMemo(() => {
    const distinctProducts = new Map<string, ProductInputDto>();

    for (const { status, categoryType, item } of items) {
      const pStatus = status?.toLowerCase();
      if (
        !item.productDescriptor ||
        !item.productClass ||
        (pStatus !== "processing" && !pStatus.includes("placed")) ||
        (categoryType !== "hardware" &&
          categoryType !== "accessories" &&
          categoryType !== "other")
      ) {
        continue;
      }

      const key = `${item.productDescriptor.toLowerCase()}::${item.productClass.toLowerCase()}`;

      if (distinctProducts.has(key)) {
        continue;
      }

      distinctProducts.set(
        key,
        new ProductInputDto({
          productDescriptor: item.productDescriptor,
          productClass: item.productClass,
          orgunitId: orgUnitId,
          quantity: item.quantity,
        }),
      );
    }

    return Array.from(distinctProducts.values());
  }, [items, orgUnitId]);

  const { data, isLoading } = useGetAvailabilityForProducts(
    orderId,
    processingItems,
  );

  const availabilityMap = useMemo(() => {
    const map = new Map<string, NonNullable<typeof data>[number]>();
    if (!data?.length) return map;
    for (const entry of data) {
      const key = `${entry.productDescriptor ?? ""}::${(entry.productClass ?? "").toLowerCase()}`;
      map.set(key, entry);
    }
    return map;
  }, [data]);

  const effectiveLines = useMemo<LineItem[]>(() => {
    if (!items.length || !availabilityMap.size) {
      return items;
    }

    return items.map((p) => {
      if (p.categoryType !== "other") {
        return p;
      }

      const found = availabilityMap.get(
        `${p.item.productDescriptor ?? ""}::${(p.item.productClass ?? "").toLowerCase()}`,
      );
      if (!found?.mediaClass) {
        return p;
      }

      const mediaClass = found.mediaClass.toLowerCase();
      const shouldRecategorize =
        mediaClass.includes("software") ||
        mediaClass.includes("license") ||
        mediaClass.includes("hardware") ||
        mediaClass.includes("accessor");
      if (!shouldRecategorize) {
        return p;
      }

      return {
        ...p,
        categoryType: getCategoryType(mediaClass),
        category: getCategoryLabel(mediaClass),
      };
    });
  }, [availabilityMap, items]);

  const isBackorder = useCallback(
    (
      productDescriptor: string | undefined,
      productClass: string | undefined,
    ) => {
      if (!availabilityMap.size) {
        return false;
      }
      const found = availabilityMap.get(
        `${productDescriptor ?? ""}::${(productClass ?? "").toLowerCase()}`,
      );
      if (found?.inStock == null) {
        return false;
      }
      // stockDataAvailable === false means no stock source returned any rows, so inStock of
      // false is "unknown" rather than a backorder.
      if (found.stockDataAvailable === false) {
        return false;
      }
      const mediaClass = (found.mediaClass ?? "").toLowerCase();
      if (mediaClass.includes("software") || mediaClass.includes("license")) {
        return false;
      }
      return !found.inStock;
    },
    [availabilityMap],
  );

  return {
    isBackorder,
    effectiveLines,
    isLoadingProductAvailability:
      isLoading && Boolean(orderId) && processingItems.length > 0,
  };
}
